import { useAppSelector } from '../../../redux/hooks';

// ─── eSigning allowlist ──────────────────────────────────────────────────
// Sourced from SESSION_INFO.betaUser.Esign — the shared PurplusWeb
// `js/BetaUser.json`, loaded into the Redux SessionInfo slice by
// Home/sessionInfoHandler.ts. Matched by firm.id (stringified), same as the
// Clio / Appcues feature gates (firm.id is a number, the list holds strings).
//
// ─── former Google Sheet implementation (commented out) ──────────────────
// Previously the allowlist was a published Google Sheet (CSV) keyed by
// firm.account (env REACT_APP_ESIGN_ALLOWLIST_CSV_URL). Retired in favour of
// BetaUser.json so the allowlist lives in one place with the other feature
// flags. Kept here for reference / quick rollback.
//
// import { useEffect, useState } from 'react';
//
// const SHEET_CSV_URL = process.env.REACT_APP_ESIGN_ALLOWLIST_CSV_URL ?? '';
//
// // firm.account was a text slug (e.g. "lddcorp"); normalized case-insensitively.
// const normalizeFirmAccount = (value: unknown): string =>
//     String(value ?? '').trim().replace(/^["']|["']$/g, '').trim().toLowerCase();
//
// let firmAccountAllowlistCache: Set<string> | null = null;
// let pendingAllowlistFetch: Promise<Set<string>> | null = null;
//
// async function fetchAllowlist(): Promise<Set<string>> {
//     if (firmAccountAllowlistCache) return firmAccountAllowlistCache;
//     if (pendingAllowlistFetch) return pendingAllowlistFetch;
//     if (!SHEET_CSV_URL) {
//         firmAccountAllowlistCache = new Set();
//         return firmAccountAllowlistCache;
//     }
//     pendingAllowlistFetch = (async () => {
//         const res = await fetch(SHEET_CSV_URL, { cache: 'no-store' });
//         if (!res.ok) throw new Error(`Allowlist fetch failed: ${res.status}`);
//         const text = await res.text();
//         const allowedAccounts = new Set<string>();
//         for (const line of text.split(/\r?\n/)) {
//             const normalized = normalizeFirmAccount(line.split(',')[0]);
//             if (normalized) allowedAccounts.add(normalized);
//         }
//         firmAccountAllowlistCache = allowedAccounts;
//         return allowedAccounts;
//     })();
//     try {
//         return await pendingAllowlistFetch;
//     } finally {
//         pendingAllowlistFetch = null;
//     }
// }
//
// // No longer needed — betaUser is loaded once by sessionInfoHandler.ts.
// export function prefetchEsignAllowlist(): void {
//     fetchAllowlist().catch(() => { /* errors are surfaced by the hook */ });
// }
// ─────────────────────────────────────────────────────────────────────────

/**
 * Whether the current firm can use eSigning, per SESSION_INFO.betaUser.Esign.
 *
 * An entry in the list enables a firm when it matches ANY of:
 *   - `firm.id`           — a single firm, e.g. "1882"
 *   - `appProvinceShort`  — a whole province, e.g. "ON"
 *   - `appCode`           — a whole app, e.g. "RPW"
 * plus the sentinel "ALL" for everyone.
 *
 * The province / appCode entries are how a jurisdiction gets switched on from
 * the JSON alone, with no code change or deploy — the same convention the
 * `AI` and `ClientConnect` lists already use (they carry both forms side by
 * side, e.g. "ON" and "RPW", so either spelling works).
 *
 * `loading` is always false: betaUser is already in the Redux SessionInfo
 * slice by the time any gated component renders, so there is no async fetch.
 * The `{ allowed, loading }` shape is unchanged so call sites compile.
 */
export function useEsignAllowlist(): { allowed: boolean; loading: boolean } {
    const sessionInfo = useAppSelector((state: any) => state.SessionInfo);
    const allowListRaw = sessionInfo?.betaUser?.Esign;
    const allowList = new Set(Array.isArray(allowListRaw) ? allowListRaw.map(String) : []);

    const allowed = allowList.has('ALL') || [
        sessionInfo?.firm?.id,
        sessionInfo?.appProvinceShort,
        sessionInfo?.appCode,
    ].some((key) => key !== null && key !== undefined && key !== '' && allowList.has(String(key)));

    return { allowed, loading: false };
}
